// src/components/Dashboard/CustomRichTextEditor.js
import React, { useRef } from 'react';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Placeholder from '@tiptap/extension-placeholder';
import Youtube from '@tiptap/extension-youtube';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import { ResizableImage } from './ResizableImage';
import { CustomTable } from './CustomTable';
import { TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Tooltip } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';

// icons
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import ContentCutIcon from '@mui/icons-material/ContentCut';

const CustomRichTextEditor = ({ value, onChange, showToolbar = true }) => {
    const inputImageRef = useRef();
    const inputVideoRef = useRef();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ textStyle: true }),
            Underline,
            Highlight.configure({ multicolor: true }),
            Superscript,
            Subscript,
            ResizableImage,
            Link,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Youtube.configure({
                controls: true,
                width: 640,
                height: 360,
                allowFullscreen: true,
                HTMLAttributes: { class: 'youtube-video' },
            }),
            Placeholder.configure({ placeholder: 'Nhập nội dung...' }),
            TextStyle,
            Color,
            FontFamily.configure({ types: ['textStyle'] }),
            CustomTable.configure({
                resizable: true,
                allowTableNodeSelection: true,
                allowCellMerge: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    const insertImage = (e) => {
        const file = e.target.files[0];
        if (!file || !editor) return;
        const reader = new FileReader();
        reader.onload = () => {
            editor.commands.setImage({ src: reader.result });
        };
        reader.readAsDataURL(file);
    };

    const insertVideo = (e) => {
        const file = e.target.files[0];
        if (!file || !editor) return;
        const reader = new FileReader();
        reader.onload = () => {
            editor.commands.insertContent(`
        <video controls width="640">
          <source src="${reader.result}" />
          Trình duyệt của bạn không hỗ trợ video.
        </video>
      `);
        };
        reader.readAsDataURL(file);
    };

    return (
        <>
            <style>{`
        .toolbar-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: none;
          background: transparent;
          margin: 2px;
        }
        .toolbar-group {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-right: 8px;
        }
      `}</style>

            <RichTextEditor
                editor={editor}
                style={{
                    border: '1px solid #ccc',
                    borderRadius: 8,
                    fontFamily: 'sans-serif',
                    fontSize: '16px',
                    minWidth: isMobile ? 300 : 1000
                }}
            >
                {/* always render toolbar, hide with display:none if showToolbar=false */}
                <RichTextEditor.Toolbar
                    style={{
                        flexWrap: 'wrap',
                        display: showToolbar ? 'flex' : 'none'
                    }}
                >
                    {/* Font / color */}
                    <div className="toolbar-group">
                        <select
                            onChange={(e) => editor?.chain().focus().setFontFamily(e.target.value).run()}
                            style={{ height: 36 }}
                        >
                            <option value="">Font</option>
                            <option value="Arial">Arial</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Tahoma">Tahoma</option>
                            <option value="Times New Roman">Times New Roman</option>
                            <option value="Courier New">Courier New</option>
                        </select>

                        <Tooltip title="Chọn màu chữ">
                            <input
                                type="color"
                                onChange={(e) => editor?.chain().focus().setColor(e.target.value).run()}
                            />
                        </Tooltip>

                        <Tooltip title="Chọn màu nền chữ">
                            <input
                                type="color"
                                onChange={(e) => editor?.chain().focus().setHighlight({ color: e.target.value }).run()}
                            />
                        </Tooltip>
                    </div>

                    {/* Văn bản */}
                    <div className="toolbar-group">
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().toggleBold().run()}><FormatBoldIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalicIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlinedIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughSIcon fontSize="small" /></button>
                    </div>

                    {/* Align */}
                    <div className="toolbar-group">
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().setTextAlign('left').run()}><FormatAlignLeftIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().setTextAlign('center').run()}><FormatAlignCenterIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().setTextAlign('right').run()}><FormatAlignRightIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().setTextAlign('justify').run()}><FormatAlignJustifyIcon fontSize="small" /></button>
                    </div>

                    {/* Insert */}
                    <div className="toolbar-group">
                        <button className="toolbar-btn" onClick={() => inputImageRef.current?.click()}><ImageIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => inputVideoRef.current?.click()}><MovieIcon fontSize="small" /></button>
                    </div>

                    {/* Table */}
                    <div className="toolbar-group">
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableChartIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().addColumnAfter().run()}><AddIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().addRowAfter().run()}><AddIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().deleteColumn().run()}><DeleteIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().deleteRow().run()}><DeleteIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().mergeCells().run()}><MergeTypeIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().splitCell().run()}><ContentCutIcon fontSize="small" /></button>
                        <button className="toolbar-btn" onClick={() => editor.chain().focus().deleteTable().run()}><DeleteIcon fontSize="small" /></button>
                    </div>
                </RichTextEditor.Toolbar>

                <RichTextEditor.Content style={{ minHeight: 350, padding: 12 }} />
            </RichTextEditor>

            {/* hidden inputs for media */}
            <input ref={inputImageRef} type="file" accept="image/*" hidden onChange={insertImage} />
            <input ref={inputVideoRef} type="file" accept="video/*" hidden onChange={insertVideo} />
        </>
    );
};

export default CustomRichTextEditor;
